import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Upload, Menu, X, LogOut, LayoutDashboard, Ship, UserCircle, Anchor, Mail } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/stores/auth';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { MyBoatsModal } from '@/components/settings/MyBoatsModal';

/**
 * The bar was hard-coded light (`bg-white`, `text-slate-700`, a light
 * `BrandLogo`) from when the whole public site was light. Home, Landing and the
 * footer are dark now, so it read as a white strip pinned above a dark page.
 *
 * It follows the shell's ground instead: dark everywhere by default, light only
 * on the pages that opted out in `PublicLayout`. One prop rather than a second
 * copy of the component, so the two versions cannot drift.
 */
interface PublicNavProps {
  /** Match the light ground — legal pages and the printable race report. */
  light?: boolean;
}

export function PublicNav({ light = false }: PublicNavProps) {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuthStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [myBoatsOpen, setMyBoatsOpen] = useState(false);

  // Listen for "open-my-boats" custom events (dispatched from ProfilePage)
  useEffect(() => {
    const handler = () => setMyBoatsOpen(true);
    window.addEventListener('open-my-boats', handler);
    return () => window.removeEventListener('open-my-boats', handler);
  }, []);

  /**
   * The ONE privilege check in this component.
   *
   * Same predicate `RootPage` uses, deliberately — an admin now lands on the
   * sailor dashboard like everybody else, and this nav item is the only thing
   * that gets them to the console from there. Two different spellings of "is
   * this an admin" is how one surface ends up showing the link to people the
   * other one hides it from.
   */
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const displayName = user?.name ?? 'User';
  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  /**
   * Every surface-dependent class in one place. Naming them by ROLE (bar, link,
   * quiet) rather than by colour means a future palette change edits this object
   * and nothing else in the file.
   */
  const s = light
    ? {
        bar: 'bg-white border-slate-200',
        link: 'text-slate-700 hover:bg-slate-50',
        outline: 'border-slate-200 bg-white hover:bg-slate-50',
        outlineIcon: 'text-slate-500',
        outlineText: 'text-slate-500',
        trigger: 'hover:bg-slate-50',
        quiet: 'text-slate-500 hover:text-navy-900',
        drawer: 'bg-white',
        drawerRule: 'border-slate-200',
        burger: 'text-slate-600',
      }
    : {
        bar: 'bg-viewer-panel border-viewer-line/20',
        link: 'text-viewer-muted hover:bg-viewer-rise/60 hover:text-viewer-ink',
        outline: 'border-viewer-line/30 bg-viewer-rise/50 hover:bg-viewer-rise',
        outlineIcon: 'text-viewer-accent',
        outlineText: 'text-viewer-ink',
        trigger: 'hover:bg-viewer-rise/60',
        quiet: 'text-viewer-muted hover:text-viewer-ink',
        drawer: 'bg-viewer-deep',
        drawerRule: 'border-viewer-line/20',
        burger: 'text-viewer-muted',
      };

  return (
    <header
      className={`${s.bar} border-b h-16 flex items-center px-4 md:px-6 sticky top-0 z-50`}
    >
      <div className="flex items-center gap-3 md:gap-6 w-full">
        {/* Logo — `variant` names the SURFACE it sits on, not the ink. */}
        <Link to="/" className="shrink-0">
          <BrandLogo variant={light ? 'light' : 'dark'} size="sm" className="md:hidden" />
          <BrandLogo variant={light ? 'light' : 'dark'} className="hidden md:inline-flex" />
        </Link>

        {/* Primary nav */}
        <nav className="hidden md:flex items-center gap-1">
          {isAuthenticated && (
            <Link to="/" className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${s.link}`}>
              Home
            </Link>
          )}
          <Link
            to="/regattas"
            className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${s.link}`}
          >
            Regattas
          </Link>
          {isAuthenticated && (
            <Link
              to="/my-races"
              className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${s.link}`}
            >
              My races
            </Link>
          )}
          {/* Admins only. The console is one click from the sailor view, and
              the sailor view is now where admins land. */}
          {isAuthenticated && isAdmin && (
            <Link
              to="/admin"
              className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${s.link}`}
            >
              Admin
            </Link>
          )}
        </nav>

        {/* Right: Actions */}
        <div className="flex items-center gap-2 md:gap-4 shrink-0 ml-auto">
          {isAuthenticated ? (
            <>
              {/* Create your own race.
                  This used to be an "Upload" button pointing at
                  /admin/sessions/new — an ADMIN route, in the PUBLIC nav, shown
                  to every signed-in account. A sailor clicking it hit an error,
                  which is the worst possible answer to "I have tracks, take
                  them". /create-race is the flow actually built for that.
                  Removed for admins too: they reach Quick Start from inside
                  /admin, so the admin route loses nothing by leaving here. */}
              <Link
                to="/create-race"
                className={`hidden md:flex items-center gap-2 px-4 h-9 rounded-lg border transition-colors ${s.outline}`}
                title="Send us your GPS tracks and we'll build the replay"
              >
                <Upload size={16} className={s.outlineIcon} />
                <span className={`text-[13px] font-medium ${s.outlineText}`}>Create your own race</span>
              </Link>

              {/* Avatar dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger
                  className={`flex items-center gap-1 rounded-md transition-colors px-1 py-0.5 ${s.trigger}`}
                >
                  <div className="group bg-brand-blue w-10 h-10 rounded-full flex items-center justify-center hover:bg-navy-900 transition-colors">
                    <span className="font-semibold text-sm text-navy-900 group-hover:text-white">{initials}</span>
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {/* Identity first — you always know which account you're in. */}
                  <div className="border-b border-slate-100 px-3 py-2.5">
                    <p className="truncate text-[13px] font-semibold text-navy-900">{user?.name}</p>
                    <p className="truncate text-[11.5px] text-slate-500">{user?.email}</p>
                  </div>
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <UserCircle className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setMyBoatsOpen(true)}>
                    <Ship className="mr-2 h-4 w-4" />
                    My boats
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/onboarding')}>
                    <Anchor className="mr-2 h-4 w-4" />
                    Claim another boat
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a href="mailto:hello@regattareplay.com">
                      <Mail className="mr-2 h-4 w-4" />
                      Email us
                    </a>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')}>
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      Admin console
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  {/* The exit, last and unmissable. */}
                  <DropdownMenuItem
                    className="text-red-600 focus:text-red-700"
                    onClick={() => {
                      logout();
                      navigate('/');
                    }}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/login" className={`text-[13px] font-medium transition-colors ${s.quiet}`}>
                Sign In
              </Link>
              <Link
                to="/signup"
                className="px-3 md:px-4 h-9 bg-brand-blue !text-navy-900 text-[13px] font-semibold rounded-lg hover:bg-navy-900 hover:!text-white transition-colors flex items-center"
              >
                Sign Up
              </Link>
            </>
          )}

          {/* Mobile menu button */}
          <button
            className={`md:hidden p-1.5 ${s.burger}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle navigation menu"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileMenuOpen && (
        <div className={`fixed inset-0 top-16 z-40 md:hidden ${s.drawer}`}>
          <nav className="flex flex-col p-5 gap-1">
            <Link
              to="/regattas"
              onClick={() => setMobileMenuOpen(false)}
              className={`px-4 py-3 rounded-lg font-semibold text-sm transition-all ${s.link}`}
            >
              Regattas
            </Link>
            {isAuthenticated && (
              <Link
                to="/my-races"
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-3 rounded-lg font-semibold text-sm transition-all ${s.link}`}
              >
                My races
              </Link>
            )}
            {isAuthenticated && (
              <>
                <Link
                  to="/create-race"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-lg font-semibold text-sm transition-all ${s.link}`}
                >
                  Create your own race
                </Link>
                {/* Admins only — same label and same predicate as the top bar. */}
                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`px-4 py-3 rounded-lg font-semibold text-sm transition-all ${s.link}`}
                  >
                    Admin
                  </Link>
                )}
                <div className={`mt-3 border-t pt-3 ${s.drawerRule}`} />
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    logout();
                    navigate('/login');
                  }}
                  className={`px-4 py-3 rounded-lg font-medium text-sm text-left transition-all ${s.link}`}
                >
                  Sign Out
                </button>
              </>
            )}
          </nav>
        </div>
      )}
      {isAuthenticated && (
        <MyBoatsModal open={myBoatsOpen} onOpenChange={setMyBoatsOpen} />
      )}
    </header>
  );
}
